/**
 * ClientDetailsSection Component
 * 
 * Simple flat layout matching original design
 * Supports both legacy FailedUnits and new ELFUnitDetails format
 */

import React from "react";
import styles from "./FlowReport.module.css";
import { FlowConfig } from "./FlowReport.utils";

interface ELFUnitDetail {
  Unit: string;
  SummaryStatus: string;
  StatusDescription?: string;
  UnitOutcome?: string;
}

interface ClientDetailsSectionProps {
  row: Record<string, any>;
  flowsConfig: FlowConfig[];
  reportMode: "single" | "multi";
}

/**
 * Format value for display
 */
const formatValue = (value: any, type?: string): string => {
  if (value === null || value === undefined || value === "") return "—";

  if (type === "date" || type === "datetime") {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return type === "date" 
          ? date.toLocaleDateString() 
          : date.toLocaleString();
      }
    } catch {
      // Fall through
    }
  }
  
  return String(value);
};

/**
 * Render ELF Unit Details section
 */
const renderELFUnitDetails = (
  elfUnitDetails: ELFUnitDetail[] | null | undefined,
  legacyFailedUnits: string | null | undefined
) => {
  // New format: ELFUnitDetails array
  if (elfUnitDetails && Array.isArray(elfUnitDetails) && elfUnitDetails.length > 0) {
    return (
      <div className={styles.elfUnitDetailsSection}>
        <span className={styles.summaryLabel}>eFile Unit Details</span>
        <div className={styles.elfUnitDetailsTable}>
          <table>
            <thead>
              <tr>
                <th>Unit</th>
                <th>Outcome</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {elfUnitDetails.map((unit, index) => {
                const outcome = (unit.UnitOutcome || unit.SummaryStatus || "").toLowerCase();
                const isRejected = outcome === "rejected";
                const isAccepted = outcome === "accepted";

                return (
                  <tr key={index}>
                    <td className={styles.unitCode}>{unit.Unit}</td>
                    <td>
                      {unit.UnitOutcome ? (
                        <span
                          className={`${styles.statusBadge} ${
                            isRejected
                              ? styles.rejected
                              : isAccepted
                              ? styles.accepted
                              : styles.pending
                          }`}
                        >
                          {unit.UnitOutcome}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{unit.StatusDescription || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Legacy format: simple string (but not "None")
  if (legacyFailedUnits && legacyFailedUnits.toLowerCase() !== "none") {
    return (
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>eFile Rejection Reason</span>
        <span className={styles.summaryValue}>{legacyFailedUnits}</span>
      </div>
    );
  }

  return null;
};
const ClientDetailsSection: React.FC<ClientDetailsSectionProps> = ({
  row,
  flowsConfig,
  reportMode,
}) => {
  // Get overall status
  const overallStatus = row.overallStatus || row.reportType || "";

  // Get ELF Unit Details (new format) and FailedUnits (legacy)
  const elfUnitDetails = row.eFileAcceptance_ELFUnitDetails;
  const legacyFailedUnits = row.eFileAcceptance_FailedUnits;

  return (
    <div className={styles.summarySection}>
      <h4 className={styles.summarySectionTitle}>Summary</h4>

      <div className={styles.summaryGrid}>
        {/* Base fields - always shown */}
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Client ID</span>
          <span className={styles.summaryValue}>{row.clientId || "—"}</span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Client Name</span>
          <span className={styles.summaryValue}>{row.clientName || "—"}</span>
        </div>

        {/* Overall Status */}
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Overall Status</span>
          <span className={styles.summaryValue}>{overallStatus || "—"}</span>
        </div>

        {/* Multi-flow: Flatten all flow fields */}
        {reportMode === "multi" &&
          flowsConfig.map((flowConfig) => {
            const alias = flowConfig.alias;

            return flowConfig.fields
              ?.filter((f) => {
                // Skip ELFUnitDetails and FailedUnits - handled separately below
                if (f.source === "ELFUnitDetails" || f.source === "FailedUnits") {
                  return false;
                }
                return f.visible !== false;
              })
              .map((field) => {
                const fieldKey = `${alias}_${field.source}`;
                const value = row[fieldKey];

                // Skip empty values
                if (value === null || value === undefined || value === "") {
                  return null;
                }

                return (
                  <div key={fieldKey} className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>{field.header}</span>
                    <span className={styles.summaryValue}>
                      {formatValue(value, field.type)}
                    </span>
                  </div>
                );
              });
          })}
      </div>

      {/* ELF Unit Details Section (separate from grid) */}
      {reportMode === "multi" && renderELFUnitDetails(elfUnitDetails, legacyFailedUnits)}
    </div>
  );
};

export default ClientDetailsSection;